import { SelectableOptionMenuItemType, Text } from '@fluentui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IBwjInputProps } from '../../../../framework/components/bwj-input-base';
import { BwjSingleSelectField } from '../../../../framework/components/bwj-select-field/single';
import { BwjWarningLabel } from '../../../../framework/components/bwj-warning-label';
import { IBwjFormOption } from '../../../../framework/entities/bwj-form-option';
import { BwjInput } from '../../../../framework/entities/bwj-input';
import { BwjValidityState } from '../../../../framework/entities/bwj-validity-state';
import { isNullOrWhitespace } from '../../../../framework/utils';
import { firstOrDefault } from '../../../../framework/utils/collection';
import { HOUR_IN_MILLISECONDS, DAY_IN_MIILISECONDS, MINUTE_IN_MILLISECONDS } from '../../../../framework/utils/date-time';
import { xlationContext, xlate } from '../../../../framework/utils/i18n';
import { usePRIVATE_STYLES } from './styles';

xlationContext('End time selection component');

interface IEndTimeSelectorProps extends IBwjInputProps<number> {
    minDateTime: number;
    maxDateTime: number;
    twelveHourDisplay: boolean;
}

interface IHourOption {
  dateTime: number;
  hour: number;
  display: string;
}

const BLUR_LATENCY = 100;

const generateHourOptions = (minDateTime: number, maxDateTime: number, twelveHourDisplay: boolean): IHourOption[] => {
  const options: IHourOption[] = [];
  const min = Math.floor(minDateTime / HOUR_IN_MILLISECONDS) * HOUR_IN_MILLISECONDS;
  let hourId = (min % DAY_IN_MIILISECONDS) / HOUR_IN_MILLISECONDS;
  for(let i = min; i <= maxDateTime; i += HOUR_IN_MILLISECONDS) {
    const hour24 = hourId < 24 ? hourId : hourId % 24;
    let displayHour = hour24;
    if(twelveHourDisplay && hour24 === 0) {
      displayHour = 12;
    } else if (twelveHourDisplay) {
      displayHour = hour24 < 13 ? hour24 : hour24 - 12;
    }

    // add dividers in hour selector
    if(i > min) {
      if(twelveHourDisplay && hour24 === 12) {
        options.push({
          dateTime: -hourId,
          hour: hour24,
          display: xlate('Afternoon'),
        });
      } else if (hour24 === 0) {
        options.push({
          dateTime: -hourId,
          hour: hour24,
          display: xlate('Next Day'),
        });
      }
    }

    options.push({
      dateTime: i,
      hour: hour24,
      display: twelveHourDisplay ? displayHour.toString() : displayHour.toString().padStart(2, '0'),
    });

    hourId++;
  }

  return options;
}

const generateMinuteOptions = (from: number, to: number): IBwjFormOption<number>[] => {
  const options: IBwjFormOption<number>[] = [];
  for(let i = from; i <= to; i++) {
    options.push({
      value: i,
      description: i.toString().padStart(2, '0'),
    });
  }

  return options;
}

const parseTime = (dateTime: number): [number, number] => {
  const hour = Math.floor(dateTime / HOUR_IN_MILLISECONDS) * HOUR_IN_MILLISECONDS;
  const minute = Math.floor((dateTime - hour) / MINUTE_IN_MILLISECONDS);

  return [hour, minute];
}

const hourOptionToInput = (option: IHourOption | null) => new BwjInput<IHourOption | null>(option);
const minuteOptionToInput = (option: IBwjFormOption<number> | null) => new BwjInput<IBwjFormOption<number> | null>(option);

export const EndTimeSelector: React.FunctionComponent<IEndTimeSelectorProps> = (props) => {
  const { input, onUpdate, twelveHourDisplay, minDateTime, maxDateTime } = props;
  const [hourInput, setHourInput] = useState(hourOptionToInput(null));
  const [minInput, setMinInput] = useState(minuteOptionToInput(null));
  const [hourOptions, setHourOptions] = useState<IHourOption[]>([]);
  const [minuteOptions, setMinuteOptions] = useState<IBwjFormOption<number>[]>([]);
  const focusTimeout = useRef<number | undefined>();
  const gotFocus = useRef(false);

  const [minHour, minMinute] = parseTime(minDateTime);
  const [maxHour, maxMinute] = parseTime(maxDateTime);

  const setInputsAndOptions = useCallback((hour: number, minute: number,
    setHour: boolean, setMinute: boolean) => {
    const minRange = [0, 59];

    if(hour === minHour) { minRange[0] = minMinute; }
    else if(hour === maxHour) { minRange[1] = maxMinute; }

    const hourOptions = generateHourOptions(minHour, maxHour, twelveHourDisplay);
    setHour && setMinute && setHourOptions(hourOptions);

    const minuteOptions = generateMinuteOptions(minRange[0], minRange[1]);
    setHour && setMinuteOptions(minuteOptions);

    if(setHour) {
      const opt = firstOrDefault(hourOptions, o => o.dateTime === hour)!
      setHourInput(hourOptionToInput(opt));
    }

    let minOpt = firstOrDefault(minuteOptions, o => o.value === minute);
    if(setMinute || !minOpt) {
      minOpt = minOpt ?? (minute < minuteOptions[0].value ? minuteOptions[0] : minuteOptions[minuteOptions.length - 1]);
      setMinInput(minuteOptionToInput(minOpt));
    }
  }, [minHour, minMinute, maxHour, maxMinute, twelveHourDisplay]);

  useEffect(() => {
    let dt = input.value;
    dt = dt < minDateTime ? minDateTime + MINUTE_IN_MILLISECONDS : dt;
    dt = dt > maxDateTime ? maxDateTime : dt;
    // trigger a value update if the above mutates the original value.
    // To avoid nesting calls to render, we'll send the update after a short delay to
    // allow this render cycle to complete
    if(dt !== input.value) {
      window.setTimeout(() => !!onUpdate && onUpdate(dt), 10);
      return;
    }

    const [hour, minute] = parseTime(dt);
    setInputsAndOptions(hour, minute, true, true);
  }, [input, minDateTime, maxDateTime, setInputsAndOptions, onUpdate]);

  const styles = usePRIVATE_STYLES();

  const onFocus = () => {
    if(focusTimeout.current !== undefined) { 
      window.clearTimeout(focusTimeout.current);
      focusTimeout.current = undefined;
    }

    if(gotFocus.current === false) {
      gotFocus.current = true;

      !!props.onFocus && props.onFocus();
    }
  }

  const onBlur = () => {
    !!props.onBlur && props.onBlur();
  }

  const onChildrenBlur = () => {
    if(focusTimeout.current !== undefined) { 
      window.clearTimeout(focusTimeout.current);
      focusTimeout.current = undefined;
    }

    !!props.onUpdate && props.onUpdate(hourInput.value!.dateTime + (minInput.value!.value * MINUTE_IN_MILLISECONDS));
    focusTimeout.current = window.setTimeout(onBlur, BLUR_LATENCY);
  }

  const onHourChange = (hour: IHourOption | null) => {
    setInputsAndOptions(hour!.dateTime, minInput.value!.value, true, false);
  }

  const onMinuteChange = (minute: IBwjFormOption<number> | null) => {
    setInputsAndOptions(hourInput.value!.dateTime, minute!.value, false, true);
  }

  const renderInputMessage = () => {
    if(isNullOrWhitespace(input.message)) { return null; }
    const msg = input.message!;

    switch(input.validity) {
      case BwjValidityState.Warning:
        return <BwjWarningLabel text={msg} />;
      case BwjValidityState.Invalid:
        return <Text variant='small' className={styles.errorMessage}>{msg}</Text>;
      default:
        return <Text variant='small' className={styles.message}>{msg}</Text>
    }
  }

  return (
    <div id={`${props.id}-root`} className={styles.root}>
      <label htmlFor={props.id}>
        <Text variant='medium' className={props.required ? styles.requiredLabel : styles.label}>{props.label}</Text>
      </label>
      <div id={props.id} className={styles.inputContainer}>
        <BwjSingleSelectField
          id={`${props.id}-hh`}
          required={false}
          onUpdate={onHourChange}
          onFocus={onFocus}
          onBlur={onChildrenBlur}
          input={hourInput}
          options={hourOptions}
          optionDisplayKey={'display'}
          getOptionItemTypeFunc={(option: IHourOption) => option.dateTime < 0 ? SelectableOptionMenuItemType.Header : SelectableOptionMenuItemType.Normal}
          optionValueKey={'dateTime'}
          suppressValueUpdateOnOptionsChange={true}
        />
        <span>:</span>
        <BwjSingleSelectField
          id={`${props.id}-mm`}
          required={false}
          onUpdate={onMinuteChange}
          onFocus={onFocus}
          onBlur={onChildrenBlur}
          input={minInput}
          options={minuteOptions}
          optionDisplayKey={'description'}
          optionValueKey={'value'}
          suppressValueUpdateOnOptionsChange={true}
        />
        {twelveHourDisplay && !!hourInput.value &&
        <span>
          {hourInput.value.hour < 12 ? xlate('AM') : xlate('PM')}
        </span>}
      </div>
      {!!input.message &&
      <div>
        {renderInputMessage()}
      </div>}
    </div>
  );
};